import { isFeatureFlagEnabled } from '@doordash/lib-feature-flags'
import { CountryCode } from '@doordash/lib-i18n'
import {
  camelCaseObjectKeys,
  snakeCaseObjectKeys,
} from '@doordash/object-utils'
import axios from 'axios'
import { get } from 'lodash'
import { SupportedCountryCode, WorkflowStepStatus } from 'src/models/types'
import * as Exp from '../experimentConstants'
import i18n from '../i18n'
import { getToken } from '../utilities/configureApi'
import { readCookie } from '../utilities/SimpleStore'
import { bffOpts, opts } from './config'
import {
  DD_DXO_ENV_KEY,
  ONBOARDING_V1_URL,
  ONBOARDING_V2_URL,
} from './constants'

export type GeoLocator = {
  zip: string
  country: CountryCode
  locale?: string
}

export type PersonLocator = {
  id?: { value: string }
  email: string
  phoneNumber: string
}

export type Onboarding = {
  onboardingId: string
  workflow: Workflow
}

export type Workflow = {
  currentModuleId: { value: string }
  currentStepId: { value: string }
  currentStepStatus: WorkflowStepStatus
  template: { value: string }
}

export type CreateOnboardingRequest = {
  geoLocator: GeoLocator
  personLocator: PersonLocator
}

export type CreateOnboardingResponse = {
  onboarding: Onboarding
  personLocator: PersonLocator
}

// We want to send US traffic to V2 Endpoints only.
// This is just a temporary solution until we completely ramp up international v1->v2
const sendRequestToMobileBff = () =>
  isFeatureFlagEnabled(Exp.DASHER_ONBOARDING_PLATFORM_BFF) ||
  isFeatureFlagEnabled(Exp.DX_BGC_ONBOARDING_PLATFORM)

const generateUpdatePayload = (uniqueLink, data, currentPageTemplate) => {
  const requestData = {
    person_locator: {
      id: uniqueLink,
    },
    current_module_id: currentPageTemplate.module_id,
    current_step_id: currentPageTemplate.step_id,
    event: {
      actions_list: Object.entries(data).map(([inputId, jsonData]) => ({
        input_id: inputId,
        json_data: get(jsonData, 'json_data'),
        input_type: inputId,
      })),
    },
  }

  return requestData
}

/*
    TODO: We dont need 2 separate payloads(actionsList vs actions_list) for VGS submit
    vs regular submit. Merge once BE updates the paths.
*/
const generateUpdatePayloadForVgs = (
  uniqueLink,
  data,
  currentPageTemplate,
  vgsFormValues
) => {
  const requestData = {
    person_locator: {
      id: uniqueLink,
    },
    current_module_id: currentPageTemplate.module_id,
    current_step_id: currentPageTemplate.step_id,
    event: {
      actions_list: Object.entries(data).map(([inputId, jsonData]) => ({
        input_id: inputId,
        json_data: { ...get(jsonData, 'json_data'), ...vgsFormValues },
        input_type: inputId,
      })),
    },
  }

  return requestData
}

export const getOnboarding = async (
  uniqueLink: string,
  countryCode: SupportedCountryCode
) => {
  const response = sendRequestToMobileBff()
    ? await axios.get(ONBOARDING_V2_URL, {
        ...bffOpts,
        params: snakeCaseObjectKeys({ uniqueLink, countryCode }),
      })
    : await axios.get(ONBOARDING_V1_URL, {
        ...opts,
        params: snakeCaseObjectKeys({ uniqueLink, countryCode }),
      })
  return camelCaseObjectKeys(response.data.onboardings)
}

export const createOnboarding = async (
  data: CreateOnboardingRequest
): Promise<CreateOnboardingResponse> => {
  const response = sendRequestToMobileBff()
    ? await axios.post(ONBOARDING_V2_URL, snakeCaseObjectKeys(data), bffOpts)
    : await axios.post(
        `${ONBOARDING_V1_URL}/create`,
        snakeCaseObjectKeys(data),
        opts
      )
  return camelCaseObjectKeys(response.data)
}

export const updateOnboarding = async (
  uniqueLink,
  data,
  currentPageTemplate
) => {
  const requestData = generateUpdatePayload(
    uniqueLink,
    data,
    currentPageTemplate
  )

  try {
    const response = sendRequestToMobileBff()
      ? await axios.patch(ONBOARDING_V2_URL, requestData, bffOpts)
      : await axios.post(ONBOARDING_V1_URL, requestData, opts)
    return camelCaseObjectKeys(response.data).onboarding
  } catch (error) {
    if (error.response) {
      throw {
        status: error.response.status,
        message: error.response.data,
      }
    }
  }
}

export const updateOnboardingViaVGS = async (
  uniqueLink,
  data,
  currentPageTemplate,
  form
) => {
  const useV2 = sendRequestToMobileBff()
  const res = await new Promise((resolve, reject) => {
    form.submit(
      useV2
        ? `/in/dxmobile${ONBOARDING_V2_URL}`
        : `/in/portal/dx-api${ONBOARDING_V1_URL}`,
      {
        method: useV2 ? 'patch' : 'post',
        mapDotToObject: 'mergeArray',
        headers: {
          Authorization: `${getToken(true)}`,
          dd_dxo_env: `${readCookie(DD_DXO_ENV_KEY)}`,
          'accept-language': `${i18n.getCurrentLocale()}`,
        },
        data: formValues =>
          generateUpdatePayloadForVgs(
            uniqueLink,
            data,
            currentPageTemplate,
            formValues
          ),
      },
      (status, response) => {
        if (status > 399) {
          reject({
            status,
            message: response,
          })
        } else {
          resolve(camelCaseObjectKeys(response).onboarding)
        }
      },
      error => {
        reject(error)
      }
    )
  })

  return res
}
